import { useState, useEffect, useRef, useCallback, forwardRef, type CSSProperties } from 'react'
import { ShieldCheck, Loader2, XCircle, CheckCircle, ArrowLeft, AlertTriangle, RefreshCw, Copy, Key } from 'lucide-react'
import { toast } from 'sonner'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { ProviderMode } from '@/pages/ProviderMode'

interface ProviderAuthGateProps {
  onBack: () => void
}

type GateState =
  | { phase: 'checking' }
  | { phase: 'setup-name' }
  | { phase: 'setup-qr';      deviceId: string; secret: string; qrBase64: string; name: string }
  | { phase: 'setup-confirm'; deviceId: string; secret: string; qrBase64: string; name: string; confirming: boolean; error: string | null }
  | { phase: 'setup-backup-codes'; backupCodes: string[] }
  | { phase: 'verify';        verifying: boolean; error: string | null }
  | { phase: 'error';         message: string }
  | { phase: 'unlocked' }

export function ProviderAuthGate({ onBack }: ProviderAuthGateProps) {
  const { proxyUrl, proxyToken } = useProxyConfig()
  const [state, setState] = useState<GateState>({ phase: 'checking' })
  const [code, setCode] = useState('')
  const [deviceName, setDeviceName] = useState('')
  const codeRef = useRef<HTMLInputElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  const headers: Record<string, string> = {
    ...(proxyToken ? { Authorization: `Bearer ${proxyToken}` } : {}),
    'Content-Type': 'application/json',
  }

  const loadSetupQr = useCallback(async (name: string) => {
    if (!proxyUrl) return
    setState({ phase: 'checking' })
    try {
      const params = name.trim() ? `?name=${encodeURIComponent(name.trim())}` : ''
      const r = await fetch(`${proxyUrl}/local/provider-auth/setup${params}`, { headers })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const data = await r.json() as { deviceId: string; secret: string; qr_base64: string; otpauth_uri: string; name: string }
      setState({ phase: 'setup-qr', deviceId: data.deviceId, secret: data.secret, qrBase64: data.qr_base64, name: data.name })
    } catch {
      setState({ phase: 'error', message: 'No se pudo generar el código QR. Verifica que el servidor esté activo e intenta de nuevo.' })
    }
  }, [proxyUrl, proxyToken]) // eslint-disable-line react-hooks/exhaustive-deps

  const startFlow = useCallback(async () => {
    if (!proxyUrl) return
    if (!proxyToken) {
      setState({ phase: 'error', message: 'Token de acceso no disponible. Reinicia la aplicación.' })
      return
    }
    setState({ phase: 'checking' })

    const MAX_RETRIES = 5
    const BASE_DELAY_MS = 800
    const BACKOFF_FACTOR = 1.5

    async function fetchStatus(): Promise<{ configured: boolean } | null> {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const r = await fetch(`${proxyUrl!}/local/provider-auth/status`, { headers })
          if (!r.ok) return null  // HTTP error: server is up but reporting a problem — don't retry
          return await r.json() as { configured: boolean }
        } catch {
          // Network error: server may still be starting up
          if (attempt < MAX_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, BASE_DELAY_MS * Math.pow(BACKOFF_FACTOR, attempt)))
            continue
          }
        }
      }
      return null
    }

    const data = await fetchStatus()
    if (!data) {
      setState({ phase: 'error', message: 'No se pudo conectar con el servidor. Verifica que la aplicación esté activa e intenta de nuevo.' })
      return
    }
    if (data.configured) {
      setState({ phase: 'verify', verifying: false, error: null })
    } else {
      setState({ phase: 'setup-name' })
    }
  }, [proxyUrl, proxyToken, loadSetupQr]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    startFlow()
  }, [proxyUrl, proxyToken]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onBack()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onBack])

  useEffect(() => {
    if (state.phase === 'verify' || state.phase === 'setup-confirm') {
      setTimeout(() => codeRef.current?.focus(), 50)
    }
    if (state.phase === 'setup-name') {
      setTimeout(() => nameRef.current?.focus(), 50)
    }
  }, [state.phase])

  async function handleConfirmSetup() {
    if (state.phase !== 'setup-confirm') return
    if (code.length !== 6) return
    setState({ ...state, confirming: true, error: null })
    try {
      const r = await fetch(`${proxyUrl}/local/provider-auth/setup/confirm`, {
        method: 'POST', headers,
        body: JSON.stringify({ deviceId: state.deviceId, secret: state.secret, name: state.name, code }),
      })
      const data = await r.json() as { success?: boolean; error?: string; backupCodes?: string[] }
      if (data.success) {
        if (data.backupCodes?.length) {
          setState({ phase: 'setup-backup-codes', backupCodes: data.backupCodes })
        } else {
          setState({ phase: 'unlocked' })
        }
        setCode('')
      } else {
        setState({ ...state, confirming: false, error: data.error ?? 'Código incorrecto' })
        setCode('')
        codeRef.current?.focus()
      }
    } catch {
      setState({ ...state, confirming: false, error: 'Error de red' })
    }
  }

  async function handleVerify() {
    if (state.phase !== 'verify') return
    const isReady = code.length === 6 || code.length === 14
    if (!isReady) return
    setState({ ...state, verifying: true, error: null })
    try {
      const r = await fetch(`${proxyUrl}/local/provider-auth/verify`, {
        method: 'POST', headers,
        body: JSON.stringify({ code }),
      })
      const data = await r.json() as { valid?: boolean; usedBackupCode?: boolean; error?: string }
      if (data.valid) {
        if (data.usedBackupCode) {
          // Fetch remaining count and show toast after unlocking
          fetch(`${proxyUrl}/local/provider-auth/backup-codes/status`, { headers })
            .then(res => res.json() as Promise<{ remaining: number; lowWarning: boolean }>)
            .then(s => {
              const msg = s.remaining === 0
                ? 'Código de respaldo usado. Ya no tienes más códigos — regenera desde el panel.'
                : `Código de respaldo usado. Te quedan ${s.remaining} código${s.remaining !== 1 ? 's' : ''}.`
              toast.warning(msg)
            })
            .catch(() => toast.warning('Código de respaldo usado.'))
        }
        setState({ phase: 'unlocked' })
      } else {
        setState({ phase: 'verify', verifying: false, error: 'Código incorrecto. Intenta de nuevo.' })
        setCode('')
        codeRef.current?.focus()
      }
    } catch {
      setState({ phase: 'verify', verifying: false, error: 'Error de red' })
    }
  }

  if (state.phase === 'unlocked') {
    return <ProviderMode onBack={onBack} />
  }

  const btnStyle = { background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0c1c2e 0%, #0b2d1f 45%, #0e1a32 100%)' }}
    >
      {/* Mesh orbs */}
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full blur-3xl pointer-events-none"
           style={{ background: 'radial-gradient(circle, hsl(160 84% 39% / 0.3) 0%, transparent 70%)' }} />
      <div className="absolute -bottom-40 -left-20 w-[400px] h-[400px] rounded-full blur-3xl pointer-events-none"
           style={{ background: 'radial-gradient(circle, #4f46e5 0%, transparent 70%)', opacity: 0.2 }} />

      <div className="relative w-full max-w-sm">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center backdrop-blur-sm">
              <span className="text-white font-bold text-xs">IS</span>
            </div>
            <div>
              <p className="font-semibold text-white text-sm leading-none">Modo Proveedor</p>
              <p className="text-white/40 text-xs mt-0.5">TecnoInnovate</p>
            </div>
          </div>
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/80 transition-colors px-2.5 py-1.5 rounded-lg hover:bg-white/8"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Volver
          </button>
        </div>

        {/* ── Checking ── */}
        {state.phase === 'checking' && (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-white/30" />
          </div>
        )}

        {/* ── Error ── */}
        {state.phase === 'error' && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-6 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/25 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-4.5 h-4.5 text-amber-400" style={{ width: '1.125rem', height: '1.125rem' }} />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base mb-1">Error de conexión</h2>
                <p className="text-white/50 text-sm leading-relaxed">{state.message}</p>
              </div>
            </div>
            <button onClick={startFlow} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-semibold transition-all active:scale-[0.98]" style={btnStyle}>
              <RefreshCw className="w-4 h-4" />
              Reintentar
            </button>
          </div>
        )}

        {/* ── Setup: nombre del dispositivo ── */}
        {state.phase === 'setup-name' && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-6 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-4.5 h-4.5 text-blue-400" style={{ width: '1.125rem', height: '1.125rem' }} />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base mb-1">Configura el acceso</h2>
                <p className="text-white/50 text-sm leading-relaxed">
                  Asigna un nombre a este dispositivo para identificarlo en tu app autenticadora.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-white/40 text-xs font-medium uppercase tracking-wide">
                Nombre del cliente / instalación
              </label>
              <input
                ref={nameRef}
                type="text"
                value={deviceName}
                onChange={e => setDeviceName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadSetupQr(deviceName)}
                placeholder="ej. Gym Alvarez"
                maxLength={40}
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent transition-all"
              />
              <p className="text-white/25 text-xs">
                Este nombre aparecerá en Google Authenticator para identificar la instalación.
              </p>
            </div>

            <button
              onClick={() => loadSetupQr(deviceName)}
              className="w-full py-3 rounded-xl text-white text-sm font-semibold transition-all active:scale-[0.98]"
              style={btnStyle}
            >
              Generar QR
            </button>
          </div>
        )}

        {/* ── Setup: QR ── */}
        {state.phase === 'setup-qr' && (
          <SetupQrCard
            state={state}
            btnStyle={btnStyle}
            onContinue={() => { setCode(''); setState({ phase: 'setup-confirm', deviceId: state.deviceId, secret: state.secret, qrBase64: state.qrBase64, name: state.name, confirming: false, error: null }) }}
            onBack={() => setState({ phase: 'setup-name' })}
          />
        )}

        {/* ── Setup: confirm code ── */}
        {state.phase === 'setup-confirm' && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-6 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-4.5 h-4.5 text-blue-400" style={{ width: '1.125rem', height: '1.125rem' }} />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base mb-1">Confirmar configuración</h2>
                <p className="text-white/50 text-sm leading-relaxed">
                  Ingresa el código de 6 dígitos de tu autenticadora para activar la protección.
                </p>
              </div>
            </div>

            <OtpInput
              ref={codeRef}
              value={code}
              onChange={v => { setCode(v); if (state.phase === 'setup-confirm') setState({ ...state, error: null }) }}
              onSubmit={handleConfirmSetup}
              disabled={state.confirming}
              error={state.error}
              totpOnly
            />

            <button
              onClick={handleConfirmSetup}
              disabled={code.length !== 6 || state.confirming}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
              style={btnStyle}
            >
              {state.confirming && <Loader2 className="w-4 h-4 animate-spin" />}
              Activar protección
            </button>

            <button
              onClick={() => { setCode(''); setState({ phase: 'setup-qr', deviceId: state.deviceId, secret: state.secret, qrBase64: state.qrBase64, name: state.name }) }}
              className="text-center text-xs text-white/35 hover:text-white/70 transition-colors"
            >
              ← Ver QR de nuevo
            </button>
          </div>
        )}

        {/* ── Setup: backup codes ── */}
        {state.phase === 'setup-backup-codes' && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-5 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center shrink-0">
                <Key className="w-4.5 h-4.5 text-emerald-400" style={{ width: '1.125rem', height: '1.125rem' }} />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base mb-1">Guarda tus códigos de respaldo</h2>
                <p className="text-white/50 text-sm leading-relaxed">
                  Úsalos si pierdes acceso a tu autenticadora. Cada código solo funciona una vez.
                </p>
              </div>
            </div>

            <div className="bg-black/20 border border-white/10 rounded-xl p-4">
              <div className="grid grid-cols-2 gap-2">
                {state.backupCodes.map((c, i) => (
                  <div key={i} className="font-mono text-sm text-white/80 bg-white/5 border border-white/8 rounded-lg px-3 py-2 text-center tracking-wider">
                    {c}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-2 bg-amber-500/8 border border-amber-500/20 rounded-xl px-3 py-2.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-amber-400/80 text-xs leading-relaxed">
                <strong className="text-amber-400">No se mostrarán de nuevo.</strong> Cópialos o imprímelos y guárdalos en un lugar seguro.
              </p>
            </div>

            <button
              onClick={() => {
                const text = state.backupCodes.join('\n')
                navigator.clipboard.writeText(text).then(() => toast.success('Códigos copiados al portapapeles'))
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white/70 text-sm font-medium border border-white/12 hover:bg-white/8 transition-all"
            >
              <Copy className="w-3.5 h-3.5" />
              Copiar todos
            </button>

            <button
              onClick={() => setState({ phase: 'unlocked' })}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-semibold transition-all active:scale-[0.98]"
              style={btnStyle}
            >
              <CheckCircle className="w-4 h-4" />
              Ya los guardé → Entrar
            </button>
          </div>
        )}

        {/* ── Verify ── */}
        {state.phase === 'verify' && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-6 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-4.5 h-4.5 text-blue-400" style={{ width: '1.125rem', height: '1.125rem' }} />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base mb-1">Acceso protegido</h2>
                <p className="text-white/50 text-sm leading-relaxed">
                  Ingresa el código de tu autenticadora o un código de respaldo.
                </p>
              </div>
            </div>

            <OtpInput
              ref={codeRef}
              value={code}
              onChange={v => { setCode(v); if (state.phase === 'verify') setState({ ...state, error: null }) }}
              onSubmit={handleVerify}
              disabled={state.verifying}
              error={state.error}
            />

            <button
              onClick={handleVerify}
              disabled={(code.length !== 6 && code.length !== 14) || state.verifying}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
              style={btnStyle}
            >
              {state.verifying && <Loader2 className="w-4 h-4 animate-spin" />}
              Verificar y entrar
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── SetupQrCard sub-component ────────────────────────────────────────────────

interface SetupQrCardProps {
  state: Extract<GateState, { phase: 'setup-qr' }>
  btnStyle: CSSProperties
  onContinue: () => void
  onBack: () => void
}

function SetupQrCard({ state, btnStyle, onContinue, onBack }: SetupQrCardProps) {
  const [showKey, setShowKey] = useState(false)
  // Format secret as groups of 4 uppercase chars for easy manual entry
  const formattedSecret = state.secret.toUpperCase().replace(/(.{4})/g, '$1 ').trim()

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-6 backdrop-blur-sm">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-4.5 h-4.5 text-blue-400" style={{ width: '1.125rem', height: '1.125rem' }} />
        </div>
        <div>
          <h2 className="text-white font-semibold text-base mb-1">Escanea el código QR</h2>
          <p className="text-white/50 text-sm leading-relaxed">
            Abre <span className="text-white/75 font-medium">Google Authenticator</span> y escanea este QR. Aparecerá como <span className="text-white/75 font-medium">{state.name}</span>.
          </p>
        </div>
      </div>

      <div className="flex justify-center">
        <div className="bg-white p-3.5 rounded-2xl shadow-xl">
          <img src={state.qrBase64} alt="QR TOTP" className="w-48 h-48 block" />
        </div>
      </div>

      {/* Manual setup key toggle */}
      <div>
        <button
          type="button"
          onClick={() => setShowKey(v => !v)}
          className="w-full flex items-center justify-center gap-2 text-xs text-white/40 hover:text-white/70 transition-colors py-1"
        >
          <Key className="w-3.5 h-3.5" />
          {showKey ? 'Ocultar clave de configuración' : 'No tengo cámara — ingresar clave manualmente'}
        </button>

        {showKey && (
          <div className="mt-3 flex flex-col gap-3">
            <div className="bg-black/25 border border-white/10 rounded-xl p-4 flex flex-col gap-2">
              <p className="text-white/35 text-xs uppercase tracking-wide font-medium">Clave de configuración</p>
              <p className="font-mono text-sm text-white/90 tracking-widest leading-relaxed break-all text-center">
                {formattedSecret}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(state.secret).then(() => toast.success('Clave copiada'))}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs text-white/50 border border-white/10 hover:bg-white/8 hover:text-white/80 transition-all"
              >
                <Copy className="w-3 h-3" />
                Copiar
              </button>
            </div>
            <p className="text-white/25 text-xs text-center leading-relaxed">
              En Google Authenticator: <strong className="text-white/40">Añadir cuenta → Ingresar clave de configuración</strong>
            </p>
          </div>
        )}
      </div>

      <button
        onClick={onContinue}
        className="w-full py-3 rounded-xl text-white text-sm font-semibold transition-all active:scale-[0.98]"
        style={btnStyle}
      >
        Ya lo configuré → Ingresar código
      </button>

      <button
        onClick={onBack}
        className="text-center text-xs text-white/35 hover:text-white/70 transition-colors"
      >
        ← Cambiar nombre
      </button>
    </div>
  )
}

// ── OtpInput sub-component ────────────────────────────────────────────────────

interface OtpInputProps {
  value: string
  onChange: (v: string) => void
  onSubmit: () => void
  disabled: boolean
  error: string | null
  totpOnly?: boolean
}

const OtpInput = forwardRef<HTMLInputElement, OtpInputProps>(
  ({ value, onChange, onSubmit, disabled, error, totpOnly }, ref) => {
    const isBackupMode = !totpOnly && value.length > 0 && /[A-Z]/i.test(value)
    const isReady = value.length === 6 || (!totpOnly && value.length === 14)
    const placeholder = isBackupMode ? 'XXXX-XXXX-XXXX' : '000 000'

    function handleChange(raw: string) {
      if (totpOnly) {
        onChange(raw.replace(/\D/g, '').slice(0, 6))
        return
      }
      const strippedDashes = raw.replace(/-/g, '')
      if (/^\d*$/.test(strippedDashes)) {
        // TOTP mode
        onChange(strippedDashes.slice(0, 6))
      } else {
        // Backup code mode
        const clean = strippedDashes.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 12)
        if (clean.length <= 4) onChange(clean)
        else if (clean.length <= 8) onChange(`${clean.slice(0, 4)}-${clean.slice(4)}`)
        else onChange(`${clean.slice(0, 4)}-${clean.slice(4, 8)}-${clean.slice(8)}`)
      }
    }

    return (
      <div className="flex flex-col gap-2">
        <input
          ref={ref}
          type="text"
          inputMode={totpOnly ? 'numeric' : 'text'}
          maxLength={totpOnly ? 6 : 14}
          value={value}
          onChange={e => handleChange(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && isReady && onSubmit()}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="one-time-code"
          className={`w-full bg-white/5 border rounded-xl px-4 py-4 text-center text-2xl font-mono text-white tracking-[0.4em] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent transition-all disabled:opacity-40 placeholder:text-white/15 ${
            error ? 'border-red-500/60' : 'border-white/10'
          }`}
        />
        {error && (
          <div className="flex items-center justify-center gap-1.5 text-red-400 text-xs">
            <XCircle className="w-3.5 h-3.5 shrink-0" />
            {error}
          </div>
        )}
        {!error && isReady && (
          <div className="flex items-center justify-center gap-1.5 text-emerald-400 text-xs">
            <CheckCircle className="w-3.5 h-3.5" />
            Listo — presiona Enter o el botón
          </div>
        )}
        {!totpOnly && !error && !isReady && value.length === 0 && (
          <p className="text-center text-white/25 text-xs">Código de 6 dígitos o código de respaldo</p>
        )}
      </div>
    )
  }
)
OtpInput.displayName = 'OtpInput'
